import React, { useEffect, useMemo, useState } from "react";
import ManufacturerNav from "../components/ManufacturerNav";
import portalAccountingApi from "../services/portalAccountingApi";
import "../styles/PortalBusiness.css";

function formatMoney(value) {
  const n = Number(value || 0);
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function PortalAccountingPage() {
  const links = useMemo(
    () => [
      { label: "Dashboard", to: "/portal/dashboard", perm: "portal.dashboard.read" },
      { label: "POS Terminals", to: "/portal/pos/terminals", perm: "pos.terminal.read" },
      { label: "POS Inventory", to: "/portal/pos-inventory", perm: "pos.inventory.submit.read" },
      // Show Accounting link if user has ANY accounting permission
      // (ManufacturerNav typically hides links when perm missing)
      { label: "Accounting", to: "/portal/accounting", perm: "accounting.journals.read" },
      { label: "Purchasing", to: "/portal/purchasing", perm: "purchasing.po.read" },
    ],
    []
  );

  const [tab, setTab] = useState("journals"); // journals | trial | pnl | tax
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [journals, setJournals] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  // Keep filters empty by default (same behavior as admin: show everything unless user filters)
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [trialRows, setTrialRows] = useState([]);
  const [pnl, setPnl] = useState({ revenue: 0, expenses: 0, netProfit: 0 });
  const [tax, setTax] = useState({ total: 0, byBranch: [], account: null });

  async function loadJournals() {
    setLoading(true);
    setError("");
    try {
      const res = await portalAccountingApi.listJournalEntries({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        limit: 50,
        offset: 0,
      });
      if (!res?.ok) throw new Error(res?.error || "load_failed");
      setJournals(res.rows || []);
      setTotal(Number(res.total || 0));
    } catch (e) {
      setError(e?.response?.data?.error || "Unable to load journal entries");
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id) {
    setLoading(true);
    setError("");
    try {
      const res = await portalAccountingApi.getJournalEntry(id);
      if (!res?.ok) throw new Error(res?.error || "load_failed");
      setDetail(res);
    } catch (e) {
      setError(e?.response?.data?.error || "Unable to load journal entry");
    } finally {
      setLoading(false);
    }
  }

  async function loadTrial() {
    setLoading(true);
    setError("");
    try {
      const res = await portalAccountingApi.getTrialBalance({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      if (!res?.ok) throw new Error(res?.error || "load_failed");
      setTrialRows(res.rows || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Unable to load trial balance");
    } finally {
      setLoading(false);
    }
  }

  async function loadPnL() {
    setLoading(true);
    setError("");
    try {
      const res = await portalAccountingApi.getPnL({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      if (!res?.ok) throw new Error(res?.error || "load_failed");
      setPnl({
        revenue: res.revenue || 0,
        expenses: res.expenses || 0,
        netProfit: res.netProfit || 0,
      });
    } catch (e) {
      setError(e?.response?.data?.error || "Unable to load P&L");
    } finally {
      setLoading(false);
    }
  }

  async function loadTax() {
    setLoading(true);
    setError("");
    try {
      const res = await portalAccountingApi.getTaxSummary({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      if (!res?.ok) throw new Error(res?.error || "load_failed");
      setTax({ total: res.total || 0, byBranch: res.byBranch || [], account: res.account || null });
    } catch (e) {
      setError(e?.response?.data?.error || "Unable to load tax summary");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setSelectedId(null);
    setDetail(null);

    if (tab === "journals") loadJournals();
    if (tab === "trial") loadTrial();
    if (tab === "pnl") loadPnL();
    if (tab === "tax") loadTax();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <section className="ucx-section ucx-portal-page">
      <div className="ucx-container">
        <ManufacturerNav title="Accounting" subtitle="Journals, Trial Balance, P&L, Tax Summary" links={links} />

        <div className="ucx-portal-panel">
          <div className="ucx-portal-head">
            <div>
              <h1 className="ucx-portal-title">Accounting</h1>
              <p className="ucx-portal-sub">Journals, Trial Balance, Profit &amp; Loss, Tax Summary</p>
            </div>
          </div>

          {error ? <div className="ucx-portal-error">{error}</div> : null}
          {loading ? <div className="ucx-muted">Loading…</div> : null}

          <div className="ucx-portal-card-large">
            <div className="ucx-portal-lineitems-head">
              <div className="ucx-portal-panel-title">Reports</div>
              <div className="ucx-portal-actions">
                <button
                  className="ucx-btn ucx-btn-outline"
                  onClick={() => setTab("journals")}
                  type="button"
                >
                  Journals
                </button>
                <button
                  className="ucx-btn ucx-btn-outline"
                  onClick={() => setTab("trial")}
                  type="button"
                >
                  Trial Balance
                </button>
                <button
                  className="ucx-btn ucx-btn-outline"
                  onClick={() => setTab("pnl")}
                  type="button"
                >
                  P&amp;L
                </button>
                <button
                  className="ucx-btn ucx-btn-outline"
                  onClick={() => setTab("tax")}
                  type="button"
                >
                  Tax Summary
                </button>
              </div>
            </div>

            {/* Filters (same default behavior as admin: empty shows all) */}
            {tab === "journals" || tab === "trial" || tab === "pnl" || tab === "tax" ? (
              <div className="ucx-portal-toolbar">
                <div className="ucx-portal-toolbar-hint">Optional date filters (leave blank to show all).</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <div className="ucx-portal-field" style={{ margin: 0 }}>
                    <label>From</label>
                    <input
                      className="ucx-portal-input-compact"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="ucx-portal-field" style={{ margin: 0 }}>
                    <label>To</label>
                    <input
                      className="ucx-portal-input-compact"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                  <button
                    className="ucx-btn ucx-btn-primary"
                    type="button"
                    onClick={tab === "journals" ? loadJournals : tab === "trial" ? loadTrial : tab === "pnl" ? loadPnL : loadTax}
                  >
                    Reload
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {!loading && tab === "journals" ? (
            <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
              <div className="ucx-portal-lineitems-head">
                <div className="ucx-portal-panel-title">Journal Entries</div>
                <div className="ucx-portal-badge">Total: {total}</div>
              </div>

              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table" style={{ minWidth: 860 }}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Entry #</th>
                      <th>Date</th>
                      <th>Source</th>
                      <th>Source ID</th>
                      <th>Memo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(journals || []).length ? (
                      (journals || []).map((j) => {
                        return (
                          <tr
                            key={j.id}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setSelectedId(j.id);
                              loadDetail(j.id);
                            }}
                          >
                            <td>{j.id}</td>
                            <td>{j.entry_no || "-"}</td>
                            <td>{j.entry_date || "-"}</td>
                            <td>{j.source_module || "-"}</td>
                            <td>{j.source_id || "-"}</td>
                            <td>{j.memo || "-"}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="ucx-muted">
                          No journal entries found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {selectedId && detail ? (
                <div style={{ marginTop: 14 }}>
                  <div className="ucx-portal-panel-title">Entry Details — {detail?.entry?.entry_no || selectedId}</div>
                  <div className="ucx-portal-tablewrap">
                    <table className="ucx-portal-table" style={{ minWidth: 860 }}>
                      <thead>
                        <tr>
                          <th>Account</th>
                          <th>Line Memo</th>
                          <th style={{ textAlign: "right" }}>Debit</th>
                          <th style={{ textAlign: "right" }}>Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.lines || []).length ? (
                          (detail.lines || []).map((l, idx) => (
                            <tr key={idx}>
                              <td>{l.account_code || l.accountCode || "-"}</td>
                              <td>{l.memo || "-"}</td>
                              <td style={{ textAlign: "right" }}>{formatMoney(l.debit)}</td>
                              <td style={{ textAlign: "right" }}>{formatMoney(l.credit)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4} className="ucx-muted">
                              No lines returned for this entry.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {!loading && tab === "trial" ? (
            <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
              <div className="ucx-portal-panel-title">Trial Balance</div>
              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table" style={{ minWidth: 860 }}>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th style={{ textAlign: "right" }}>Debit</th>
                      <th style={{ textAlign: "right" }}>Credit</th>
                      <th style={{ textAlign: "right" }}>Debit Balance</th>
                      <th style={{ textAlign: "right" }}>Credit Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(trialRows || []).length ? (
                      (trialRows || []).map((r) => (
                        <tr key={r.id}>
                          <td>{r.code}</td>
                          <td>{r.name}</td>
                          <td>{r.type}</td>
                          <td style={{ textAlign: "right" }}>{formatMoney(r.debit_total)}</td>
                          <td style={{ textAlign: "right" }}>{formatMoney(r.credit_total)}</td>
                          <td style={{ textAlign: "right" }}>{formatMoney(Math.max(Number(r.net || 0), 0))}</td>
                          <td style={{ textAlign: "right" }}>{formatMoney(Math.max(-Number(r.net || 0), 0))}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="ucx-muted">
                          No trial balance rows returned.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {!loading && tab === "pnl" ? (
            <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
              <div className="ucx-portal-panel-title">Profit &amp; Loss</div>
              <div className="ucx-kpi-grid">
                <div className="ucx-portal-card">
                  <div className="ucx-portal-card-label">Revenue</div>
                  <div className="ucx-portal-card-value">{formatMoney(pnl?.revenue)}</div>
                </div>
                <div className="ucx-portal-card">
                  <div className="ucx-portal-card-label">Expenses</div>
                  <div className="ucx-portal-card-value">{formatMoney(pnl?.expenses)}</div>
                </div>
                <div className="ucx-portal-card">
                  <div className="ucx-portal-card-label">Net</div>
                  <div className="ucx-portal-card-value">{formatMoney(pnl?.netProfit)}</div>
                </div>
              </div>
            </div>
          ) : null}

          {!loading && tab === "tax" ? (
            <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
              <div className="ucx-portal-panel-title">Tax Summary</div>
              <div className="ucx-portal-toolbar" style={{ marginTop: 10 }}>
                <div className="ucx-portal-toolbar-hint">
                  Account: {tax?.account?.code ? `${tax.account.code} — ${tax.account.name}` : "N/A"} | Total: {formatMoney(tax?.total)}
                </div>
              </div>
              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table" style={{ minWidth: 720 }}>
                  <thead>
                    <tr>
                      <th>Branch</th>
                      <th style={{ textAlign: "right" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(tax?.byBranch || []).length ? (
                      (tax.byBranch || []).map((b) => (
                        <tr key={b.branch_id}>
                          <td>{b.branch_id === 0 ? "N/A" : b.branch_id}</td>
                          <td style={{ textAlign: "right" }}>{formatMoney(b.total)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={2} className="ucx-muted">
                          No tax summary returned.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
